<template>
  <EChartCard
    :title="$t('completeness')"
    :subtitle="$t('clickToFilterMissing')"
    :buildOption="buildOption"
    :height="360"
    @chart-click="handleChartClick"
  />
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import EChartCard from '@/components/charts/base/EChartCard.vue';
import { getSequential, statusColors } from '@/utils/vizPalette';
import { axisColor } from '@/utils/chartTheme';
import { useSelectableFilter } from '@/composables/useSelectableFilter';
import { REGISTER_FIELDS } from '@/utils/assetFields';

const store = useStore();
const { t } = useI18n();

const chartName = 'completenessChart';
const { isSelected, hasSelection, sync, toggle } = useSelectableFilter(chartName);

// Only the register fields this project actually configured - the spec asks
// for gaps in the customer's own Asset Register, not in fields they never use.
const hasAssetField = computed(() => store.getters.hasAssetField);
const activeFields = () => REGISTER_FIELDS.filter((field) => hasAssetField.value(field.alias));

// dataIndex -> field, rebuilt each render because the bars are sorted.
const barFields = ref([]);

const buildOption = async () => {
  sync();
  const conn = store.getters.getDBConnection;
  if (!conn) return null;

  const fields = activeFields();
  if (fields.length === 0) return null;

  // Each column is cast to VARCHAR before NULLIF so blank strings do not count
  // as populated.
  const result = await conn.query(`
    SELECT
      ${fields.map((field) => `COUNT(NULLIF(CAST(${field.alias} AS VARCHAR), '')) * 100.0 / NULLIF(COUNT(*), 0) AS ${field.alias}`).join(',\n      ')}
    FROM filteredAssets
  `);
  const row = result.toArray()[0]?.toJSON();
  if (!row) return null;

  // Ascending, because a category yAxis draws its first entry at the bottom -
  // this puts the most complete field at the top.
  const bars = fields
    .map((field) => ({ field, label: t(field.labelKey), value: Number(row[field.alias]) || 0 }))
    .sort((a, b) => a.value - b.value);
  barFields.value = bars.map((bar) => bar.field);

  const fontColor = store.getters.chartFontColor;
  const barColor = getSequential(store.state.theme)[2];
  const gridColor = axisColor(store.state.theme);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      valueFormatter: (value) => `${Math.round(value)}%`,
    },
    grid: { left: 8, right: 48, top: 8, bottom: 4, containLabel: true },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: { color: fontColor, formatter: '{value}%' },
      splitLine: { lineStyle: { color: gridColor } },
    },
    yAxis: {
      type: 'category',
      data: bars.map((bar) => bar.label),
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { show: false },
      axisLabel: { color: fontColor },
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 18,
        label: {
          show: true,
          position: 'right',
          color: fontColor,
          formatter: (params) => `${Math.round(params.value)}%`,
        },
        data: bars.map((bar) => ({
          value: bar.value,
          itemStyle: {
            // Selecting a bar shows the assets MISSING that field, so the
            // highlighted bar is the gap, not the filled portion.
            color: isSelected(bar.field.alias) ? statusColors.warning : barColor,
            borderRadius: [0, 4, 4, 0],
            opacity: hasSelection.value && !isSelected(bar.field.alias) ? 0.35 : 1,
          },
        })),
      },
    ],
  };
};

const handleChartClick = async (params) => {
  if (params.componentType !== 'series' || params.dataIndex === undefined) return;
  const field = barFields.value[params.dataIndex];
  if (!field) return;

  await toggle(
    field.alias,
    `NULLIF(CAST(${field.alias} AS VARCHAR), '') IS NULL`,
    `${t('missing')}: ${t(field.labelKey)}`
  );
};
</script>
